import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  catchError,
  concatMap,
  EMPTY,
  from,
  last,
  of,
  Subscription,
  tap,
} from 'rxjs';
import {
  ConstructionDayReport,
  ConstructionDayReportType,
  NodeItem,
  ReportCreationResult,
  StateService,
  TimeEntry,
  ToolService,
  WorkingEntry,
} from 'projects/shared/src/public-api';
import { ModalController } from '@ionic/angular';
import { UploadDocumentRequest } from '../../models/upload-document-reqest';
import { ModalRole } from '../../models/modal-roles';

@Component({
  selector: 'lib-construction-day-report-form',
  templateUrl: './construction-day-report-form.component.html',
  styleUrls: ['./construction-day-report-form.component.scss'],
  standalone: false,
})
export class ConstructionDayReportFormComponent implements OnInit, OnDestroy {
  constructor(
    public state: StateService,
    public tools: ToolService,
    private modalCtrl: ModalController
  ) {}

  public allConSiteReports: ConstructionDayReport[] = [];
  public existingHoursInDb: WorkingEntry[];
  private subs: Subscription[] = [];
  public reports: ConstructionDayReport[] = [];
  public description: any;
  public weatherOptions: string[] = [
    'Sonne',
    'Bewölkt',
    'Wind',
    'Regen',
    'Schnee',
    'Frost',
    'Hagel',
    'Schneeregen',
    'Graupel',
    'Sturm',
    'Gewitter',
  ];
  public reportId: string;
  public errorList: string[] = [];
  public ConstructionDayReportType = ConstructionDayReportType;
  public reportType = ConstructionDayReportType.$0;
  public reportTypeLabel: string;
  public uploadRequests: UploadDocumentRequest[][] = [];
  public attachedNodeItems: NodeItem[] = [];
  public nodeItemsToDelete: string[] = [];
  private uniqueIds: string[] = [];
  public preselectedMachines: string[] = [];

  ngOnInit() {
    this.changeReportTypeLabel();
    this.subs.push(
      this.state.constructionDayReports$.subscribe((x) => {
        this.allConSiteReports = JSON.parse(JSON.stringify(x));
      })
    );
    if (this.reportId != null) {
      this.subs.push(
        this.state.constructionDayReports$.subscribe((x) => {
          this.reports.push(
            JSON.parse(JSON.stringify(x.find((x) => x.id == this.reportId)))
          );
          this.reportType = this.reports[0].type;
          this.changeReportTypeLabel();
          this.checkForInvalidFields();
        })
      );
      this.checkForExistingReports(this.reports[0]);
      this.preselectedMachines = this.reports[0].machineUsages?.map(
        (x) => x.id
      );
    } else {
      this.addNewReport();
    }
    if (this.reportId) {
      this.subs.push(
        this.state.nodeItems$.subscribe((x) => {
          this.attachedNodeItems = x.filter(
            (x) => x.constructionDayReportId == this.reportId
          );
        })
      );
    }

    this.checkForInvalidFields();
  }

  public async cancel() {
    return await this.modalCtrl.dismiss(null, 'cancel');
  }

  public changeReportTypeLabel() {
    if (this.reportType === ConstructionDayReportType.$0)
      this.reportTypeLabel = 'Bautagesbericht';
    if (this.reportType === ConstructionDayReportType.$1)
      this.reportTypeLabel = 'Fahrtenbericht';
    if (this.reportType === ConstructionDayReportType.$2)
      this.reportTypeLabel = 'Arbeitszeitbericht';

    this.reports.forEach((x) => {
      this.syncWorkersAndTimes(x);
    });

    this.checkForInvalidFields();
  }

  ngOnDestroy() {
    this.unsubscribeAll();
  }

  private unsubscribeAll() {
    this.subs?.forEach((x) => {
      x.unsubscribe();
    });
  }

  public addNewReport() {
    let newReport: ConstructionDayReport = {};
    newReport.id = '';
    newReport.constructionSite = null;
    if (this.reports.length > 0) {
      const lastWorkingTimes =
        this.reports[this.reports.length - 1].workingTimes;
      newReport.workingTimes = [
        {
          start: lastWorkingTimes[lastWorkingTimes.length - 1].end,
          end: this.tools.addOneHour(
            lastWorkingTimes[lastWorkingTimes.length - 1].end
          ),
        },
      ];
    } else {
      newReport.workingTimes = [
        {
          start: this.tools.getRoundedUtcTime(false),
          end: this.tools.addOneHour(this.tools.getRoundedUtcTime(false)),
        },
      ];
    }
    newReport.workers = [];
    newReport.date = null;
    newReport.difficulties = '';
    newReport.electricityUsedInKWH = 0;
    newReport.waterUsedInCubicMeters = 0;
    newReport.weather = [];
    newReport.temperatureInCelsius = 0;
    newReport.rapport = '';
    newReport.report = '';
    newReport.responsibleWorker = this.state.userName;
    newReport.sentDate = '';
    newReport.securityCheck = false;
    newReport.specialIncidents = '';
    newReport.approvedBy = '';
    newReport.approvedDate = new Date().toISOString();
    newReport.sentDate = new Date().toISOString();
    newReport.workerTimeEntries = {};
    newReport.errors = [];
    newReport.machineUsages = [];
    this.uniqueIds.push('');
    this.syncWorkersAndTimes(newReport);
    this.reports.push(newReport);
    this.checkForInvalidFields();
  }

  private syncWorkersAndTimes(report: ConstructionDayReport) {
    if (this.reportType != ConstructionDayReportType.$0) {
      report.workers = [report.responsibleWorker];
      report.workerTimeEntries = {};
      report.workerTimeEntries[report.responsibleWorker] = report.workingTimes;
    }
  }

  timeEntryStartChanged(
    entry: TimeEntry,
    evt: string,
    report: ConstructionDayReport
  ) {
    entry.start = evt;
    this.syncWorkersAndTimes(report);
    this.checkForInvalidFields();
  }

  timeEntryEndChanged(
    entry: TimeEntry,
    evt: string,
    report: ConstructionDayReport
  ) {
    entry.end = evt;
    this.syncWorkersAndTimes(report);
    this.checkForInvalidFields();
  }

  addTimeEntry(entries: TimeEntry[], report: ConstructionDayReport) {
    if (entries.length == 0) {
      entries.push({});
      this.checkForInvalidFields();
      return;
    }
    const lastValue = entries[entries.length - 1];
    const start = this.tools.addOneHour(lastValue.end);
    entries.push({ start: start, end: this.tools.addOneHour(start) });
    this.checkForInvalidFields();
  }

  removeTimeEntry(entries: TimeEntry[], i: number) {
    entries.splice(i, 1);
    this.checkForInvalidFields();
  }

  removeReport(i: number) {
    this.reports.splice(i, 1);
    this.checkForInvalidFields();
  }

  weatherChange(e: any, report: ConstructionDayReport) {
    report.weather = e.detail.value;
  }
  workerChange(e: any, report: ConstructionDayReport) {
    report.workers = e;
    e.forEach((x: string) => {
      if (report.workerTimeEntries[x] == null) {
        report.workerTimeEntries[x] = JSON.parse(
          JSON.stringify(report.workingTimes)
        );
      }
    });
    for (let key in report.workerTimeEntries) {
      if (!report.workers.includes(key)) delete report.workerTimeEntries[key];
    }
    this.checkForInvalidFields();
  }
  responsibleWorkerChange(e: any, report: ConstructionDayReport) {
    report.responsibleWorker = e;
    this.syncWorkersAndTimes(report);
    this.checkForInvalidFields();
  }

  conSiteChange(e: any, report: ConstructionDayReport) {
    report.constructionSite = e;
    this.checkForExistingReports(report);
    this.checkForInvalidFields();
  }

  dateChanged(evt: string, report: ConstructionDayReport) {
    report.date = evt;
    this.checkForExistingReports(report);
    this.checkForInvalidFields();
  }

  checkForExistingReports(report: ConstructionDayReport) {
    if (report.constructionSite && report.date) {
      this.subs.push(
        this.state
          .constructionDayReportExists(report.constructionSite, report.date)
          .subscribe((y) => {
            this.existingHoursInDb = y;
          })
      );
    }
  }

  async sendToBackend() {
    this.reports.forEach((x) => {
      if (!x.temperatureInCelsius) {
        x.temperatureInCelsius = 0;
      } else {
        x.temperatureInCelsius = parseInt(x.temperatureInCelsius.toString());
      }
      if (!x.electricityUsedInKWH) x.electricityUsedInKWH = 0;
      if (!x.waterUsedInCubicMeters) x.waterUsedInCubicMeters = 0;
      x.type = this.reportType;
    });

    if (this.reportId == null) this.createReports();

    if (this.reportId != null) this.updateReport(this.reports[0]);
  }

  // we could use a forkJoin here
  private async updateReport(report: ConstructionDayReport) {
    this.state
      .updateConstructionDayReport(report)
      .pipe(
        concatMap((x: string) => {
          if (
            this.uploadRequests.length === 0 ||
            this.uploadRequests[0].length === 0
          )
            return of(undefined);
          this.unsubscribeAll();
          this.tools.showInfoToast(
            `Dokumente werden hochgeladen - das kann einen Moment dauern.`
          );
          this.uploadRequests[0].forEach(
            (x) => (x.constructionDayReportId = this.reportId)
          );
          return from(this.uploadRequests[0]).pipe(
            concatMap((file) => this.state.uploadFile(file)),
            last()
          );
        }),

        catchError((err) => {
          this.state.handleError(err);
          return EMPTY;
        })
      )
      .subscribe({
        next: async (v) => {
          this.tools.showInfoToast('Bautagesbericht aktualisiert!');
          await this.modalCtrl.dismiss(null, ModalRole.Confirm);
        },
        error: (err) => this.state.handleError(err),
      });

    await this.state.deleteNodes(this.nodeItemsToDelete, false);
  }

  private createReports() {
    let failedReports: ConstructionDayReport[] = [];
    let failedUploads: UploadDocumentRequest[][] = [];
    from(this.reports)
      .pipe(
        concatMap((report, reportIndex) =>
          this.state.createConstructionDayReport(report).pipe(
            concatMap((reportCreationResult: ReportCreationResult) => {
              if (this.uploadRequests.length > 0) {
                this.tools.showInfoToast(
                  `Dokumente werden hochgeladen - das kann einen Moment dauern.`
                );
                this.uploadRequests[reportIndex].forEach(
                  (x) => (x.constructionDayReportId = reportCreationResult.id)
                );
                return from(this.uploadRequests[reportIndex]).pipe(
                  concatMap((file) => this.state.uploadFile(file)),

                  last(undefined, reportCreationResult)
                );
              } else {
                return of(reportCreationResult);
              }
            }),

            tap((x: any) => {
              this.tools.showInfoToast(
                this.generateSuccessMesage(report.type, false)
              );
            }),

            catchError((err) => {
              failedReports.push(report);
              failedUploads.push(this.uploadRequests[reportIndex]);
              let errorMessage = 'Fehler beim Absenden von Bericht';
              if (this.reports.length > 1)
                errorMessage += ` ${reportIndex + 1}`;
              this.tools.showErrorToast(errorMessage);
              return EMPTY;
            })
          )
        )
      )
      .subscribe({
        complete: () => {
          if (failedReports.length == 0) {
            this.modalCtrl.dismiss(null, ModalRole.Confirm);
          } else {
            this.reports = failedReports;
            this.uploadRequests = failedUploads;
          }
        },
      });
  }

  async sendReport() {
    this.reports.forEach((x) => (x.isDraft = false));
    await this.sendToBackend();
  }

  async createDraft() {
    let allConSitesSet = true;
    this.reports.forEach((x) => {
      x.isDraft = true;
      if (!x.constructionSite) {
        allConSitesSet = false;
      }
    });
    if (allConSitesSet) {
      await this.sendToBackend();
    } else {
      this.tools.showInfoToast('Bitte Baustelle auswählen');
    }
  }

  isTimeGreater(date1: Date, date2: Date) {
    console.log(date1);
    console.log(date2);
    const t1 =
      date1.getHours() * 3600 + date1.getMinutes() * 60 + date1.getSeconds();
    const t2 =
      date2.getHours() * 3600 + date2.getMinutes() * 60 + date2.getSeconds();
    return t1 > t2;
  }

  checkForInvalidFields() {
    this.errorList = [];
    for (let i = 0; i < this.reports.length; i++) {
      for (let j = i + 1; j < this.reports.length; j++) {
        this.reports[i].workers.forEach((x) => {
          if (this.reports[j].workers.includes(x)) {
            if (
              this.tools.arraysOverlap(
                this.reports[i].workerTimeEntries[x],
                this.reports[j].workerTimeEntries[x]
              ) &&
              this.tools.dateStringsAreSameDay(
                this.reports[i].date,
                this.reports[j].date
              )
            ) {
              this.errorList.push(
                'Arbeitszeiten von ' +
                  this.state.getUserDisplayName(x) +
                  ' in Bericht ' +
                  (i + 1) +
                  ' und Bericht ' +
                  (j + 1) +
                  ' überschneiden sich'
              );
            }
          }
        });
      }
    }
    this.reports.forEach((report, i) => {
      if (this.tools.timeEntriesInvalid(report.workingTimes)) {
        this.errorList.push('Baustellenzeiten ungültig');
      }
      if (!report.constructionSite)
        this.errorList.push('Keine Baustelle ausgewählt');
      if (
        !report.securityCheck &&
        this.reportType == ConstructionDayReportType.$0
      )
        this.errorList.push('Sicherheitscheck fehlt');
      if (!report.responsibleWorker)
        this.errorList.push('Verantworlicher Mitarbeiter fehlt');
      if (!report.report) this.errorList.push('Leistungsergebnisse fehlen');
      if (report.workingTimes.length == 0)
        this.errorList.push('Baustellenzeiten fehlen');
      if (
        report.workers.length == 0 &&
        this.reportType == ConstructionDayReportType.$0
      )
        this.errorList.push('Keine Mitarbeiter ausgewählt');
      if (this.tools.arrayHasOverlap(report.workingTimes))
        this.errorList.push('Baustellenzeiten überschneiden sich');
      if (report.workers.length > 0 && report.workingTimes.length > 0) {
        report.workers.forEach((x) => {
          if (this.timeCollisionDetected(x, report)) {
            this.errorList.push(
              'Arbeitszeit von ' +
                this.state.getUserDisplayName(x) +
                ' überschneidet sich mit bereits gebuchten Arbeitszeiten'
            );
          }
          if (this.tools.arrayHasOverlap(report.workerTimeEntries[x]))
            this.errorList.push(
              'Arbeitszeiten von ' +
                this.state.getUserDisplayName(x) +
                ' überschneiden sich'
            );

          if (report.workerTimeEntries[x].length == 0) {
            this.errorList.push(
              'Keine Zeiten für ' +
                this.state.getUserDisplayName(x) +
                ' gefunden'
            );
          }

          if (
            this.tools.hasInvalidBookedTimes(
              report.workingTimes,
              report.workerTimeEntries[x]
            )
          ) {
            this.errorList.push(
              'Arbeitszeiten von ' +
                this.state.getUserDisplayName(x) +
                ' außerhalb der Baustellenzeiten'
            );
          }
        });
      }
    });
  }

  userHasOtherConSiteWorkingTimes(
    worker: string,
    report: ConstructionDayReport
  ): boolean {
    return (
      this.existingHoursInDb?.find(
        (x) =>
          x.user == worker &&
          x.createdBy != this.reportId &&
          (x.constructionSite != report.constructionSite ||
            (x.constructionSite == report.constructionSite &&
              x.createdByReport))
      ) != null
    );
  }

  userHasWorkingTimes(worker: string, report: ConstructionDayReport): boolean {
    let usrWorkingTimes = this.existingHoursInDb?.find(
      (x) =>
        x.user == worker &&
        x.constructionSite == report.constructionSite &&
        !x.createdByReport
    );
    if (this.reportType == ConstructionDayReportType.$0) {
      return (
        usrWorkingTimes != null &&
        JSON.stringify(usrWorkingTimes.timeEntries) !=
          JSON.stringify(report.workerTimeEntries[worker])
      );
    } else {
      return (
        usrWorkingTimes != null &&
        JSON.stringify(usrWorkingTimes.timeEntries) !=
          JSON.stringify(report.workingTimes)
      );
    }
  }

  getThisConSiteWorkingTimes(
    worker: string,
    report: ConstructionDayReport
  ): WorkingEntry[] {
    let workingList = '';
    return this.existingHoursInDb.filter(
      (x) =>
        x.user == worker &&
        x.constructionSite == report.constructionSite &&
        !x.createdByReport
    );
  }

  getBeautifiedString(x: WorkingEntry) {
    return (
      'Manuell angelegte Arbeitszeit gefunden: ' +
      x.timeEntries
        .map(
          (y) =>
            this.tools.formatTime(y.start) +
            ' bis ' +
            this.tools.formatTime(y.end)
        )
        .join(', ')
    );
  }

  getOtherConSiteWorkingTimes(
    worker: string,
    report: ConstructionDayReport
  ): string[] {
    let workingList: string[] = [];
    this.existingHoursInDb
      .filter(
        (x) =>
          x.user == worker &&
          x.createdBy != this.reportId &&
          (x.constructionSite != report.constructionSite ||
            (x.constructionSite == report.constructionSite &&
              x.createdByReport))
      )
      .forEach((x) => {
        let entry =
          this.state.getConstructionSiteName(x.constructionSite) + ': ';
        entry += x.timeEntries
          .map(
            (y) =>
              this.tools.formatTime(y.start) +
              ' bis ' +
              this.tools.formatTime(y.end)
          )
          .join(', ');
        workingList.push(entry);
      });
    return workingList;
  }

  takeUserHours(
    worker: string,
    entry: WorkingEntry,
    report: ConstructionDayReport
  ) {
    report.workerTimeEntries[worker] = JSON.parse(
      JSON.stringify(entry.timeEntries)
    );
    if (this.reportType != ConstructionDayReportType.$0) {
      report.workingTimes = JSON.parse(JSON.stringify(entry.timeEntries));
    }
    if (!report.report.includes(entry.description))
      report.report += '\n' + entry.description;
    this.checkForInvalidFields();
  }

  timeCollisionDetected(
    worker: string,
    report: ConstructionDayReport
  ): boolean {
    if (!this.existingHoursInDb) return false;
    let doesOverlap = false;
    this.existingHoursInDb
      .filter(
        (x) =>
          x.user == worker && x.createdBy != this.reportId && x.createdByReport
      )
      .forEach((x) => {
        if (
          this.tools.arraysOverlap(
            x.timeEntries,
            report.workerTimeEntries[worker]
          )
        )
          doesOverlap = true;
      });
    return doesOverlap;
  }

  public weekendOrPublicHolidaySelected(date: string): boolean {
    const dt = new Date(date);
    return dt.getDay() == 0 || dt.getDay() == 6;
  }

  public setUploadFiles(
    uploadRequests: UploadDocumentRequest[],
    index: number
  ) {
    this.uploadRequests[index] = uploadRequests;
  }

  public openFileInput(index: number) {
    document.getElementById(this.uniqueIds[index]).click();
  }

  private generateSuccessMesage(
    reportType: ConstructionDayReportType,
    merged: boolean
  ): string {
    let message = '';
    if (reportType == ConstructionDayReportType.$0) {
      message =
        'Bautagesbericht erfolgreich abgeschickt! Kran sichern und alles abschließen!';
    } else {
      let reportName =
        reportType == ConstructionDayReportType.$1
          ? 'Fahrtenbericht'
          : 'Arbeitszeitbericht';
      if (merged) {
        message =
          reportName +
          ' erfolgreich abgeschickt und mit bestehendem Bericht zusammengefasst!';
      } else {
        message = reportName + ' erfolgreich abgeschickt!';
      }
    }
    return message;
  }

  public deleteNodeItem(id: string) {
    this.nodeItemsToDelete.push(id);
  }

  public setUniqueId(id: string, index: number) {
    this.uniqueIds[index] = id;
  }

  public selectedMachinesChanged(ids: string[], report: ConstructionDayReport) {
    if (report.machineUsages == null) report.machineUsages = [];
    ids.forEach((x) => {
      if (report.machineUsages.find((y) => y.id == x) == null) {
        report.machineUsages.push({ id: x, usageInDays: 0, usageInHours: 0 });
      }
    });
    report.machineUsages = report.machineUsages.filter((x) =>
      ids.includes(x.id)
    );
  }
}
